import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type React from 'react';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';

type DrawerProps = {
  title: string;
  onClose: () => void;
  children?: ReactNode;
  wide?: boolean;
  /** A drawer whose content is a two-pane console rather than a column of
   *  rows — settings. Wide enough that a label, its sentence and its control
   *  sit on one line beside a navigation rail. */
  xl?: boolean;
  /** Rendered in the header, before Close. For the one action a long drawer
   *  cannot afford to have only at the far end of it. */
  actions?: ReactNode;
  /** The child owns the padding and the scrolling. For a layout with its own
   *  scroll regions (a rail beside a pane) and a footer pinned outside them:
   *  the body's own scroller would put a second scrollbar around all three. */
  flush?: boolean;
};

/** Per-drawer, so the settings drawer and a narrower one don't fight over one
 *  number. Keyed by title because that is what distinguishes them to the user.
 *
 *  VERSIONED (v2, 2026-08-11). A stored width is an inline style, so it beats
 *  the CSS default forever — every existing user would have kept the 520px
 *  settings drawer the console layout was widened away from, and nothing on
 *  screen would explain why theirs was narrow. Bumping the key retires the old
 *  numbers once; double-clicking the handle still resets to the new default. */
const widthKey = (title: string) => `rkh.drawerWidth.v2.${title || 'drawer'}`;

/** Bounds for a dragged width. The lower bound is where the ⚙ rows stop being
 *  readable (a label, a sentence and a control side by side); the upper leaves
 *  enough canvas behind the drawer to still see what a setting is affecting. */
const MIN_W = 320;
const maxW = () => Math.max(MIN_W, Math.round(window.innerWidth * 0.9));

function readStoredWidth(title: string): number | null {
  try {
    const n = Number(localStorage.getItem(widthKey(title)));
    return Number.isFinite(n) && n >= MIN_W ? n : null;
  } catch {
    return null; // private mode / storage disabled — the CSS default stands
  }
}

/**
 * Right-edge drawer over the canvas. Escape or backdrop click closes.
 *
 * RESIZABLE FROM THE LEFT EDGE. The width was a fixed 380px (520 for `wide`),
 * which the settings drawer outgrew: its rows are a label, an explanatory
 * sentence and a control side by side, and at 380px the sentence wraps to five
 * or six lines and the whole panel reads as a column of cramped paragraphs.
 * Rather than pick a new fixed number that is wrong on a different screen, the
 * edge is draggable and the choice is remembered per drawer.
 *
 * The handle is a real button in the tab order, not just a hit area: dragging
 * is unavailable to anyone not using a mouse, so arrow keys resize it too and
 * Home/End jump to the bounds.
 */
export default function Drawer({ title, onClose, children, wide, xl, flush, actions }: DrawerProps) {
  const asideRef = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useState<number | null>(() => readStoredWidth(title));
  const dragging = useRef<boolean>(false);

  // Below 768px the drawer is full-width by design and the handle is hidden.
  // An inline width would BEAT that media query (inline styles always do), so
  // a width dragged on a desktop would follow the user onto a narrow window
  // and leave a strip of backdrop they cannot close the gap on. Tracked live
  // rather than read once, because resizing the window is how you hit it.
  // The query is PHONE_QUERY rather than a second copy of the literal: this
  // file used to spell out '(max-width: 767px)' twice on its own.
  const narrow = useMediaQuery(PHONE_QUERY);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commit = useCallback(
    (w: number) => {
      const clamped = Math.min(maxW(), Math.max(MIN_W, Math.round(w)));
      setWidth(clamped);
      try {
        localStorage.setItem(widthKey(title), String(clamped));
      } catch {
        /* not worth failing a resize over */
      }
    },
    [title],
  );

  // Pointer events, not mouse: one code path covers trackpad, mouse and touch,
  // and setPointerCapture keeps the drag alive when the cursor outruns the
  // 6px handle — which it does immediately, because the handle is 6px.
  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation(); // never let the backdrop read this as a close
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging.current) return;
    // The drawer is pinned right, so its width is the distance from the
    // pointer to the right edge of the window.
    commit(window.innerWidth - e.clientX);
  };
  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onHandleKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const current = width || asideRef.current?.offsetWidth || MIN_W;
    const step = e.shiftKey ? 64 : 16;
    // Left grows the drawer (it is pinned to the right edge), so the key and
    // the movement agree.
    if (e.key === 'ArrowLeft') commit(current + step);
    else if (e.key === 'ArrowRight') commit(current - step);
    else if (e.key === 'Home') commit(maxW());
    else if (e.key === 'End') commit(MIN_W);
    else return;
    e.preventDefault();
  };

  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <aside
        ref={asideRef}
        className={'drawer' + (xl ? ' drawer--xl' : wide ? ' drawer--wide' : '')}
        // Only once dragged, and never on a narrow window — see `narrow`.
        // Until then the CSS default is left completely alone.
        style={width && !narrow ? { width: `${width}px` } : undefined}
        role="dialog"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="drawer__resize"
          aria-label={`Resize ${title || 'drawer'}`}
          title="Drag to resize"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onHandleKey}
          onDoubleClick={() => {
            // Back to the CSS default, so a drag that went wrong is one
            // gesture to undo rather than a hunt for the right pixel.
            setWidth(null);
            try {
              localStorage.removeItem(widthKey(title));
            } catch {
              /* ignore */
            }
          }}
        />
        <header className="drawer__header">
          <h2 className="drawer__title">{title}</h2>
          <div className="drawer__actions">
            {actions}
            <button type="button" className="link" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className={'drawer__body' + (flush ? ' drawer__body--flush' : '')}>{children}</div>
      </aside>
    </div>
  );
}
